/**
 * Live Chats — where a person actually takes a website conversation over.
 *
 * Until this page existed, a visitor asking to speak to someone produced a row in
 * `ChatEscalation` and an Adaptive Card in a Teams channel that production had
 * never been configured to reach. The widget's whole handoff path terminated in
 * nothing anybody could see.
 *
 * Structure follows Support Inbox — queue, thread, visitor — because it is the
 * same job and staff should not have to learn a second layout for it. The palette
 * is the chat-widget teal rather than Support's blue, so it reads as part of the
 * widget surface it belongs to.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  MessagesSquare,
  PanelRightClose,
  PanelRightOpen,
  RefreshCw,
} from 'lucide-react';
import { Button, cn } from '@respectly/ui';
import type { ChatSessionStatusFilter } from '@respectly/shared-types';
import { useLiveChatsStore } from '../../stores/useLiveChatsStore';
import { SessionList } from './SessionList';
import { SessionThread } from './SessionThread';
import { VisitorCard } from './VisitorCard';
import { AlertToggle } from './AlertToggle';

const FILTERS: Array<{ value: ChatSessionStatusFilter; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'agent', label: 'With a colleague' },
  { value: 'all', label: 'All' },
];

/** Polled as a floor under the SSE badge: the queue stream carries counts, not rows. */
const REFRESH_INTERVAL_MS = 15_000;

export function LiveChats() {
  const navigate = useNavigate();
  // `strict: false` because the route tree nests this page under the layout
  // route, so the literal path is not a valid `from` id.
  const search = useSearch({ strict: false }) as { session?: string };

  const sessions = useLiveChatsStore((s) => s.sessions);
  const counts = useLiveChatsStore((s) => s.counts);
  const status = useLiveChatsStore((s) => s.status);
  const activeId = useLiveChatsStore((s) => s.activeId);
  const active = useLiveChatsStore((s) => s.active);
  const loadingList = useLiveChatsStore((s) => s.loadingList);
  const loadingThread = useLiveChatsStore((s) => s.loadingThread);
  const acting = useLiveChatsStore((s) => s.acting);
  const error = useLiveChatsStore((s) => s.error);
  const loadSessions = useLiveChatsStore((s) => s.loadSessions);
  const setStatus = useLiveChatsStore((s) => s.setStatus);
  const open = useLiveChatsStore((s) => s.open);
  const refreshActive = useLiveChatsStore((s) => s.refreshActive);

  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    void loadSessions();
    // The open thread rides the same timer as the queue. It used to move only
    // when the admin acted on it, so a visitor's reply to a chat you had just
    // taken over never arrived on screen.
    const timer = setInterval(() => {
      void loadSessions();
      void refreshActive();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [loadSessions, refreshActive]);

  // `?session=` is what the Teams Adaptive Card's "Open in console" button links
  // to, so a colleague reading the notification lands on the conversation rather
  // than on a queue they then have to search.
  useEffect(() => {
    if (search.session) void open(search.session);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.session]);

  const select = (id: string): void => {
    void open(id);
    void navigate({ to: '/live-chats', search: { session: id }, replace: true });
  };

  const back = (): void => {
    void open(null);
    void navigate({ to: '/live-chats', search: {}, replace: true });
  };

  const showMobileList = isMobile && !activeId;
  const showMobileDetail = isMobile && Boolean(activeId);

  return (
    <motion.div
      className="flex h-[calc(100vh-theme(spacing.16))] flex-col bg-slate-50 dark:bg-slate-950"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="shrink-0 border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div className="flex h-14 items-center justify-between px-5">
          <div className="flex items-center gap-3">
            {showMobileDetail && (
              <Button
                variant="ghost"
                size="sm"
                onClick={back}
                className="h-8 w-8 rounded-lg p-0 md:hidden"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}

            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-500/10 ring-1 ring-teal-500/20 dark:bg-teal-500/15">
              <MessagesSquare className="h-4 w-4 text-teal-600 dark:text-teal-400" />
            </div>

            <h1 className="text-sm font-semibold tracking-tight text-slate-900 dark:text-slate-50">
              Live Chats
            </h1>

            {counts && (
              <div className="ml-2 hidden items-center gap-1.5 sm:flex">
                <StatChip
                  value={counts.waiting}
                  label="waiting"
                  tone="bg-amber-50 text-amber-600 ring-amber-500/20 dark:bg-amber-950/40 dark:text-amber-400"
                  pulse={counts.waiting > 0}
                />
                <StatChip
                  value={counts.agent}
                  label="with a colleague"
                  tone="bg-teal-50 text-teal-600 ring-teal-500/20 dark:bg-teal-950/40 dark:text-teal-400"
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <AlertToggle />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void loadSessions()}
              disabled={loadingList}
              className="h-8 w-8 rounded-lg p-0"
            >
              <RefreshCw
                className={cn('h-3.5 w-3.5 text-slate-500', loadingList && 'animate-spin')}
              />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSidebar(!showSidebar)}
              className="hidden h-8 w-8 rounded-lg p-0 xl:flex"
            >
              {showSidebar ? (
                <PanelRightClose className="h-3.5 w-3.5 text-slate-500" />
              ) : (
                <PanelRightOpen className="h-3.5 w-3.5 text-slate-500" />
              )}
            </Button>
          </div>
        </div>

        {!showMobileDetail && (
          <div className="flex gap-1.5 px-5 pb-3">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => void setStatus(f.value)}
                className={cn(
                  'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                  status === f.value
                    ? 'bg-teal-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="shrink-0 border-b border-red-200 bg-red-50 px-5 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {(showMobileList || !isMobile) && (
          <div
            className={cn(
              'flex flex-col overflow-y-auto border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900',
              isMobile ? 'w-full' : 'w-80 shrink-0'
            )}
          >
            <SessionList
              sessions={sessions}
              activeId={activeId}
              loading={loadingList}
              onSelect={select}
            />
          </div>
        )}

        {(showMobileDetail || !isMobile) && (
          <div className="flex flex-1 flex-col overflow-hidden bg-slate-50 dark:bg-slate-950">
            <SessionThread
              detail={active}
              loading={loadingThread}
              acting={acting}
              onClaim={() => void useLiveChatsStore.getState().claim()}
              onReply={(text) => useLiveChatsStore.getState().reply(text)}
              onRelease={() => void useLiveChatsStore.getState().release()}
              onClose={() => void useLiveChatsStore.getState().close()}
            />
          </div>
        )}

        {showSidebar && !isMobile && active && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="w-64 shrink-0 overflow-y-auto border-l border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
          >
            <VisitorCard
              visitor={active.visitor}
              escalations={active.escalations}
              onOpenSession={select}
            />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

function StatChip({
  value,
  label,
  tone,
  pulse,
}: {
  value: number;
  label: string;
  tone: string;
  pulse?: boolean;
}) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-[11px] font-medium ring-1',
        tone,
        pulse && 'animate-pulse'
      )}
    >
      {value} {label}
    </span>
  );
}
